/**
 * Экран «Себестоимость» — §9.4, метрики `R15`–`R19`, подэтап M4 этапа 4.
 *
 * Отвечает на вопрос ежедневного контроля «где теряем деньги на
 * продуктах» тремя слоями: общий фудкост с кухней и баром отдельно,
 * фудкост по категориям и таблица блюд со светофором.
 *
 * **Светофор сравнивает блюдо с его категорией, а не с общей целью**
 * (R18): у салата и стейка нормальная маржа разная, и абсолютный порог
 * красил бы половину меню без повода.
 *
 * **Что не вошло в расчёт — на экране, а не в сноске.** Пустая техкарта
 * бизнес-ланча или цена стейка за килограмм при продаже по 100 г — это
 * задачи шефу, и именно они делают процент неверным, если их спрятать.
 * Поэтому у таблицы блюд есть отбор «Не посчитано».
 *
 * Арифметики здесь нет (§12.1): все числа, включая светофор и отклонение
 * от категории, приходят с сервера.
 */

import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button } from '@shell/ui';
import { Loading } from '@shell/states';
import { formatMoney, formatNumber } from '@shell/format';
import { financeApi } from '../api';
import type { MetricView, PeriodParams } from '../api';
import {
  DownloadLink,
  ExplanationModal,
  FreshnessBadge,
  MetricTile,
  PeriodPicker,
  ScreenError,
  Section,
  useExplanation,
} from '../components';

const CODES = ['cost.foodcost_pct', 'cost.foodcost_by_category', 'margin.by_dish'] as const;

/** Отбор таблицы блюд. */
type DishFilter = 'all' | 'worse' | 'excluded';

const LIGHTS: Record<string, string> = { green: '🟢', yellow: '🟡', red: '🔴' };

interface ExcludedGroup {
  text: string;
  dishes: number;
  revenue: string;
  top: string[];
}

function num(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pct(value: unknown): string {
  const parsed = num(value);
  return parsed === null ? '—' : `${formatNumber(parsed, 1)} %`;
}

function money(value: unknown): string {
  const parsed = num(value);
  return parsed === null ? '—' : formatMoney(parsed);
}

/** Фудкост категории — из её средней маржи: 100 − маржа %. Не расчёт, а
 *  другая запись того же числа, которое пришло с сервера. */
function categoryFoodcost(row: Record<string, unknown>): string {
  const margin = num(row.category_margin_pct);
  return margin === null ? '—' : `${formatNumber(100 - margin, 1)} %`;
}

export function Cost({
  period,
  onPeriodChange,
  onDrill,
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
  onDrill: (filters: { item?: string }) => void;
}) {
  const [filter, setFilter] = useState<DishFilter>('worse');
  const [explained, explain, closeExplanation] = useExplanation();

  const queries = useQueries({
    queries: CODES.map((code) => ({
      queryKey: ['finance', 'metric', code, period],
      queryFn: () => financeApi.metric(code, period),
    })),
  });

  if (queries.some((query) => query.isLoading)) return <Loading />;
  const failed = queries.find((query) => query.error);
  if (failed) {
    return (
      <ScreenError
        error={failed.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => queries.forEach((query) => query.refetch())}
      />
    );
  }

  const [foodcost, byCategory, dishes] = queries.map((query) => query.data as MetricView);
  const excluded = (foodcost.extras.excluded ?? {}) as Record<string, ExcludedGroup>;
  const rows = dishes.rows.filter((row) => {
    if (filter === 'worse') return row.light === 'red' || row.light === 'yellow';
    if (filter === 'excluded') return Boolean(row.flag) && (num(row.revenue) ?? 0) > 0;
    return true;
  });

  return (
    <div className="screen">
      {foodcost.freshness && <FreshnessBadge freshness={foodcost.freshness} />}
      <PeriodPicker period={period} anchor={period.to ?? null} onChange={onPeriodChange} />

      <div className="tiles">
        <MetricTile metric={foodcost} onExplain={explain} />
        <div className="tile">
          <div className="tile__name">Кухня</div>
          <div className="tile__value">{pct(foodcost.extras.kitchen_pct)}</div>
        </div>
        <div className="tile">
          <div className="tile__name">Бар</div>
          <div className="tile__value">{pct(foodcost.extras.bar_pct)}</div>
        </div>
        <div className="tile">
          <div className="tile__name">Маржа</div>
          <div className="tile__value">{money(foodcost.extras.margin)}</div>
          <p className="tile__comparisons faint">
            Посчитано по {pct(foodcost.extras.coverage_pct)} выручки
          </p>
        </div>
      </div>

      {Object.keys(excluded).length > 0 && (
        <Section title="Не вошло в расчёт — проверить">
          <ul className="plain-list">
            {Object.entries(excluded).map(([flag, group]) => (
              <li key={flag}>
                <strong>{group.text}</strong>: блюд {group.dishes} на {money(group.revenue)}
                {group.top.length > 0 && <span className="faint"> — {group.top.join(', ')}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section
        title="По категориям"
        actions={
          <>
            <Button small variant="quiet" onClick={() => explain(byCategory)}>
              Как посчитано
            </Button>
            <DownloadLink href={financeApi.csvUrl('cost.foodcost_by_category', period)}>
              CSV
            </DownloadLink>
          </>
        }
      >
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Категория</th>
                <th className="num">Продажи</th>
                <th className="num">Фудкост</th>
                <th className="num">Маржа</th>
              </tr>
            </thead>
            <tbody>
              {byCategory.rows.map((row) => (
                <tr key={String(row.category_code)}>
                  <td>{String(row.name)}</td>
                  <td className="num">{money(row.revenue)}</td>
                  <td className="num">{pct(row.foodcost_pct)}</td>
                  <td className="num">{money(row.margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Блюда"
        actions={
          <>
            <Button
              small
              variant={filter === 'worse' ? 'primary' : 'quiet'}
              onClick={() => setFilter('worse')}
            >
              Хуже категории
            </Button>
            <Button
              small
              variant={filter === 'excluded' ? 'primary' : 'quiet'}
              onClick={() => setFilter('excluded')}
            >
              Не посчитано
            </Button>
            <Button
              small
              variant={filter === 'all' ? 'primary' : 'quiet'}
              onClick={() => setFilter('all')}
            >
              Все
            </Button>
            <Button small variant="quiet" onClick={() => explain(dishes)}>
              Как посчитано
            </Button>
            <DownloadLink href={financeApi.csvUrl('margin.by_dish', period)}>CSV</DownloadLink>
          </>
        }
      >
        {rows.length === 0 ? (
          <p className="faint">
            {filter === 'worse'
              ? 'Все посчитанные блюда в пределах своей категории.'
              : 'Таких блюд за период нет.'}
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th />
                  <th>Блюдо</th>
                  <th className="num">Продано</th>
                  <th className="num">Цена</th>
                  <th className="num">Себестоимость</th>
                  <th className="num">Фудкост</th>
                  <th className="num">В категории</th>
                  <th className="num">Маржа</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={String(row.code)}>
                    <td>{row.light ? LIGHTS[String(row.light)] : '⚪'}</td>
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => onDrill({ item: String(row.code) })}
                      >
                        {String(row.name)}
                      </button>
                      {row.flag_text ? (
                        <div className="faint">{String(row.flag_text)}</div>
                      ) : null}
                    </td>
                    <td className="num">{formatNumber(num(row.quantity) ?? 0, 0)}</td>
                    <td className="num">{money(row.avg_price)}</td>
                    <td className="num">{money(row.unit_cost)}</td>
                    <td className="num">{pct(row.foodcost_pct)}</td>
                    <td className="num">{categoryFoodcost(row)}</td>
                    <td className="num">{money(row.contribution)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="faint chart-hint">
          🟢 не хуже категории больше чем на 5 п.п. · 🟡 от 5 до 15 п.п. хуже · 🔴 хуже на 15
          п.п. или в минус. Нажмите на блюдо, чтобы увидеть его чеки.
        </p>
      </Section>

      <ExplanationModal metric={explained} onClose={closeExplanation} />
    </div>
  );
}
